import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { FormInput, FormLabel } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import Alert from "@/components/Base/Alert";

type EditRegionProps = {
    mode?: "add" | "edit";
};

interface State {
    id: string;
    name: string;
    countryCode?: string;
}

interface FormData {
    name: string;
    companyId?: string;
    stateIds: string[];
}

interface FormErrors {
    name?: string;
    stateIds?: string;
}

const EMPTY_FORM: FormData = {
    name: "",
    stateIds: [],
};

function EditRegion({ mode = "edit" }: EditRegionProps) {
    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [loading, setLoading] = useState(mode === "edit");
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [states, setStates] = useState<State[]>([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [stateConflicts, setStateConflicts] = useState<Record<string, { regionName: string }>>({});
    const navigate = useNavigate();
    const { id } = useParams();
    const conflictsCheckedRef = useRef(false);

    // Load states and check conflicts
    useEffect(() => {
        const loadStates = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            try {
                setLoadingStates(true);
                const response = await axios.get(`${apiUrl}/states?countryCode=MY`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const loadedStates = response.data.data || [];
                setStates(loadedStates);

                // Check conflicts for all states
                // For ADMIN users, backend will get companyId from user scope, so we don't need it in query
                // For SUPERADMIN, we can optionally include companyId from URL params
                const urlParams = new URLSearchParams(window.location.search);
                const companyId = formData.companyId || urlParams.get("companyId");
                
                console.log('Loading states - mode:', mode, 'companyId:', companyId, 'loadedStates:', loadedStates.length);
                
                if (loadedStates.length > 0) {
                    const stateIds = loadedStates.map((s: State) => s.id);
                    try {
                        const params = new URLSearchParams();
                        stateIds.forEach((sid: string) => params.append('stateIds[]', sid));
                        // Only include regionId for edit mode
                        if (id && mode === "edit") params.append('regionId', id);
                        // Only include companyId if we have it (for SUPERADMIN), backend will use scope for ADMIN
                        if (companyId) params.append('companyId', companyId);

                        console.log('Checking conflicts in loadStates - URL:', `${apiUrl}/regions/check-state-availability?${params.toString()}`);
                        const conflictResponse = await axios.get(
                            `${apiUrl}/regions/check-state-availability?${params.toString()}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        const conflicts = conflictResponse.data.data?.conflicts || [];
                        const conflictsMap: Record<string, { regionName: string }> = {};
                        conflicts.forEach((conflict: any) => {
                            conflictsMap[conflict.stateId] = { regionName: conflict.regionName };
                        });
                        console.log('State conflicts loaded in loadStates:', conflictsMap, 'Total:', conflicts.length);
                        setStateConflicts(conflictsMap);
                    } catch (error) {
                        console.error("Failed to check state conflicts:", error);
                        // Don't clear conflicts on error, keep existing state
                    }
                } else {
                    // Clear conflicts if no states
                    setStateConflicts({});
                }
            } catch (error) {
                console.error("Failed to load states:", error);
            } finally {
                setLoadingStates(false);
            }
        };

        loadStates();
    }, [formData.companyId, id]);

    // Re-check conflicts when states are loaded (for Add mode, in case first check didn't work)
    // Use a ref to track if we've already checked to prevent infinite loops
    useEffect(() => {
        // Reset the ref when states change (new states loaded)
        // This allows re-checking when states are reloaded
        conflictsCheckedRef.current = false;
        
        const urlParams = new URLSearchParams(window.location.search);
        const companyIdFromUrl = urlParams.get("companyId");
        const companyId = formData.companyId || companyIdFromUrl;
        
        // Only check conflicts if states are loaded and we haven't checked yet
        // This prevents infinite loops when stateConflicts changes
        if (states.length > 0 && !conflictsCheckedRef.current) {
            conflictsCheckedRef.current = true; // Mark as checked to prevent re-running
            
            const checkConflicts = async () => {
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const token = getToken();
                
                try {
                    const stateIds = states.map((s: State) => s.id);
                    const params = new URLSearchParams();
                    stateIds.forEach((sid: string) => params.append('stateIds[]', sid));
                    // Don't include regionId for Add mode
                    if (id && mode === "edit") params.append('regionId', id);
                    // Only include companyId if we have it (for SUPERADMIN), backend will use scope for ADMIN
                    if (companyId) params.append('companyId', companyId);

                    console.log('Re-checking conflicts - companyId:', companyId, 'states:', states.length, 'mode:', mode);
                    const conflictResponse = await axios.get(
                        `${apiUrl}/regions/check-state-availability?${params.toString()}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    console.log('Conflict response:', conflictResponse.data);
                    const conflicts = conflictResponse.data.data?.conflicts || [];
                    const conflictsMap: Record<string, { regionName: string }> = {};
                    conflicts.forEach((conflict: any) => {
                        conflictsMap[conflict.stateId] = { regionName: conflict.regionName };
                    });
                    console.log('State conflicts loaded (re-check):', conflictsMap, 'Total conflicts:', conflicts.length);
                    setStateConflicts(conflictsMap);
                } catch (error) {
                    console.error("Failed to check state conflicts:", error);
                    conflictsCheckedRef.current = false; // Reset on error so we can retry
                }
            };
            
            // Small delay to ensure states are fully loaded
            const timeoutId = setTimeout(() => {
                checkConflicts();
            }, 200);
            
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [states, formData.companyId, id, mode]);

    useEffect(() => {
        const init = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            try {
                setLoading(true);

                // Get companyId from URL params
                const urlParams = new URLSearchParams(window.location.search);
                const companyId = urlParams.get("companyId");

                // If editing, load the region group
                if (mode === "edit" && id) {
                    const groupRes = await axios.get(`${apiUrl}/regions/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const group = groupRes.data;

                    setFormData({
                        name: group.name || "",
                        companyId: group.companyId || "",
                        stateIds: group.states?.map((s: State) => s.id) || []
                    });
                } else if (companyId) {
                    // Pre-fill companyId for new regions
                    setFormData(prev => ({ ...prev, companyId }));
                }

            } catch (error) {
                console.error("Failed to initialize:", error);
                setSubmitError("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [mode, id]);

    const handleInputChange = (field: keyof FormData, value: string | string[]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const checkStateAvailability = async (stateId: string) => {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        const token = getToken();
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = formData.companyId || urlParams.get("companyId");

        try {
            const params = new URLSearchParams();
            params.append('stateIds[]', stateId);
            if (id) params.append('regionId', id);
            // Only include companyId if we have it (for SUPERADMIN), backend will use scope for ADMIN
            if (companyId) params.append('companyId', companyId);

            const response = await axios.get(
                `${apiUrl}/regions/check-state-availability?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const conflicts = response.data.data?.conflicts || [];
            const conflict = conflicts.find((c: any) => c.stateId === stateId);

            if (conflict) {
                setStateConflicts(prev => ({
                    ...prev,
                    [stateId]: { regionName: conflict.regionName }
                }));
                return { available: false, conflict };
            } else {
                setStateConflicts(prev => {
                    const newConflicts = { ...prev };
                    delete newConflicts[stateId];
                    return newConflicts;
                });
                return { available: true, conflict: null };
            }
        } catch (error: any) {
            console.error("Failed to check state availability:", error);
            // If it's a 400 error about companyId, the backend should still work for ADMIN users
            // Try again without companyId (backend will use scope)
            if (error.response?.status === 400 && error.response?.data?.message?.includes('Company ID')) {
                try {
                    const params = new URLSearchParams();
                    params.append('stateIds[]', stateId);
                    if (id) params.append('regionId', id);
                    // Don't include companyId - let backend use scope for ADMIN

                    const response = await axios.get(
                        `${apiUrl}/regions/check-state-availability?${params.toString()}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    const conflicts = response.data.data?.conflicts || [];
                    const conflict = conflicts.find((c: any) => c.stateId === stateId);

                    if (conflict) {
                        setStateConflicts(prev => ({
                            ...prev,
                            [stateId]: { regionName: conflict.regionName }
                        }));
                        return { available: false, conflict };
                    } else {
                        setStateConflicts(prev => {
                            const newConflicts = { ...prev };
                            delete newConflicts[stateId];
                            return newConflicts;
                        });
                        return { available: true, conflict: null };
                    }
                } catch (retryError) {
                    console.error("Failed to check state availability on retry:", retryError);
                    return { available: true, conflict: null }; // Allow on error
                }
            }
            return { available: true, conflict: null }; // Allow on error
        }
    };

    const handleStateToggle = async (stateId: string) => {
        // If trying to select (not deselect), check availability first
        const isCurrentlySelected = formData.stateIds?.includes(stateId) || false;
        
        if (!isCurrentlySelected) {
            // First check if we already know about the conflict from stateConflicts
            const knownConflict = stateConflicts[stateId];
            
            if (knownConflict) {
                // State is already known to have a conflict
                const stateName = states.find(s => s.id === stateId)?.name || 'Unknown';
                const regionName = knownConflict.regionName;
                const confirmed = window.confirm(
                    `The state "${stateName}" is already assigned to region "${regionName}". Each state can only be assigned to one region per company.\n\nDo you want to assign it anyway? (It will be filtered out on submit)`
                );
                
                if (!confirmed) {
                    return; // User cancelled, don't proceed with selection
                }
                
                // User confirmed, proceed with assignment (will be filtered on submit)
                setFormData(prev => {
                    const currentIds = prev.stateIds || [];
                    return { ...prev, stateIds: [...currentIds, stateId] };
                });
                if (errors.stateIds) {
                    setErrors(prev => ({ ...prev, stateIds: undefined }));
                }
                return; // Don't continue with normal toggle flow
            }
            
            // If not in known conflicts, do a fresh check
            const checkResult = await checkStateAvailability(stateId);
            
            if (!checkResult.available && checkResult.conflict) {
                // Show warning but allow user to proceed
                const stateName = states.find(s => s.id === stateId)?.name || 'Unknown';
                const regionName = checkResult.conflict.regionName;
                const confirmed = window.confirm(
                    `The state "${stateName}" is already assigned to region "${regionName}". Each state can only be assigned to one region per company.\n\nDo you want to assign it anyway? (It will be filtered out on submit)`
                );
                
                if (!confirmed) {
                    return; // User cancelled, don't proceed with selection
                }
                
                // User confirmed, proceed with assignment
                setFormData(prev => {
                    const currentIds = prev.stateIds || [];
                    return { ...prev, stateIds: [...currentIds, stateId] };
                });
                if (errors.stateIds) {
                    setErrors(prev => ({ ...prev, stateIds: undefined }));
                }
                return; // Don't continue with normal toggle flow
            }
        }

        // Proceed with toggle (either deselecting or selecting after confirmation)
        setFormData(prev => {
            const currentIds = prev.stateIds || [];
            const newIds = currentIds.includes(stateId)
                ? currentIds.filter(id => id !== stateId)
                : [...currentIds, stateId];
            return { ...prev, stateIds: newIds };
        });
        // Clear error when user makes selection
        if (errors.stateIds) {
            setErrors(prev => ({ ...prev, stateIds: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Region name is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setSubmitError("");
        setSubmitSuccess("");

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Before submitting, do a final check for conflicts and filter them out
            const selectedStateIds = formData.stateIds || [];
            const urlParams = new URLSearchParams(window.location.search);
            const companyId = formData.companyId || urlParams.get("companyId");
            
            // Final conflict check - verify all selected states are available
            // Backend will use user scope for ADMIN users, so we can check even without companyId
            let availableStateIds = [...selectedStateIds];
            const conflictingStateIds: string[] = [];
            
            if (availableStateIds.length > 0) {
                // Check each selected state for conflicts
                for (const stateId of selectedStateIds) {
                    // First check known conflicts
                    if (stateConflicts[stateId]) {
                        conflictingStateIds.push(stateId);
                        continue;
                    }
                    
                    // Do a fresh check for this state (backend will use scope for ADMIN)
                    const checkResult = await checkStateAvailability(stateId);
                    if (!checkResult.available && checkResult.conflict) {
                        conflictingStateIds.push(stateId);
                    }
                }
                
                // Filter out conflicting states
                availableStateIds = selectedStateIds.filter(id => !conflictingStateIds.includes(id));
            }

            console.log('Submit - Selected states:', selectedStateIds);
            console.log('Submit - Conflicting states:', conflictingStateIds);
            console.log('Submit - Available states:', availableStateIds);
            console.log('Submit - State conflicts map:', stateConflicts);

            // If there were conflicts in selected states, show a warning
            if (conflictingStateIds.length > 0) {
                const conflictingStateNames = conflictingStateIds
                    .map(id => {
                        const state = states.find(s => s.id === id);
                        const conflict = stateConflicts[id];
                        return state ? `${state.name} (already in "${conflict?.regionName}")` : 'Unknown';
                    })
                    .join(', ');
                
                const proceed = window.confirm(
                    `The following state(s) are already assigned to other regions and cannot be included:\n\n${conflictingStateNames}\n\nDo you want to create the region with only the available states?`
                );

                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }

            // Prepare data for API - only include available states
            // Note: companyId should be in query string, not body (for backend compatibility)
            const apiData = {
                name: formData.name,
                stateIds: availableStateIds
            };

            console.log('Submit - API Data:', apiData);
            console.log('Submit - CompanyId:', companyId);

            if (mode === "add") {
                const queryParams = new URLSearchParams();
                if (companyId) {
                    queryParams.append("companyId", companyId);
                }

                const requestUrl = `${apiUrl}/regions?${queryParams.toString()}`;
                console.log('Submit - POST URL:', requestUrl);
                console.log('Submit - POST Body:', apiData);

                const response = await axios.post(
                    requestUrl,
                    apiData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('Submit - Response:', response.data);
                setSubmitSuccess("Region created successfully!");
            } else {
                const requestUrl = `${apiUrl}/regions/${id}`;
                console.log('Submit - PUT URL:', requestUrl);
                console.log('Submit - PUT Body:', apiData);

                const response = await axios.put(
                    requestUrl,
                    apiData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('Submit - Response:', response.data);
                setSubmitSuccess("Region updated successfully!");
            }

            // Redirect after success
            setTimeout(() => {
                const searchParams = new URLSearchParams(window.location.search);
                const projectId = searchParams.get("projectId");
                const companyId = formData.companyId;

                let redirectUrl = "/regions";
                if (projectId) {
                    redirectUrl += `?projectId=${projectId}`;
                } else if (companyId) {
                    redirectUrl += `?companyId=${companyId}`;
                }

                navigate(redirectUrl);
            }, 1500);

        } catch (error: any) {
            console.error("Submit failed:", error);
            console.error("Submit error response:", error.response?.data);
            console.error("Submit error status:", error.response?.status);
            const message = error.response?.data?.message || error.message || "An error occurred";
            setSubmitError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;

        let redirectUrl = "/regions";
        if (projectId) {
            redirectUrl += `?projectId=${projectId}`;
        } else if (companyId) {
            redirectUrl += `?companyId=${companyId}`;
        }

        navigate(redirectUrl);
    };

    if (loading && mode === "edit") {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading region...</div>
                </div>
            </div>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                {mode === "add" ? "Add Region" : "Edit Region"}
                            </div>
                        </div>

                        <div className="mt-3.5 grid grid-cols-12 xl:grid-cols-10 gap-y-7 lg:gap-y-10 gap-x-6">
                            <div className="relative flex flex-col col-span-12 lg:col-span-9 xl:col-span-8 gap-y-7">
                                <form onSubmit={handleSubmit}>
                                    <div className="flex flex-col p-5 box box--stacked">
                                        <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                                            <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                                                <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                                                Region Information
                                            </div>

                                            {submitError && (
                                                <Alert variant="outline-danger" className="mt-5">
                                                    <Lucide icon="AlertTriangle" className="w-6 h-6" />
                                                    {submitError}
                                                </Alert>
                                            )}

                                            {submitSuccess && (
                                                <Alert variant="outline-success" className="mt-5">
                                                    <Lucide icon="CheckCircle" className="w-6 h-6" />
                                                    {submitSuccess}
                                                </Alert>
                                            )}

                                            <div className="mt-5 grid grid-cols-12 gap-4">
                                                <div className="col-span-12">
                                                    <label className="inline-block mb-2">
                                                        Region Name <span className="text-danger">*</span>
                                                    </label>
                                                    <FormInput
                                                        type="text"
                                                        placeholder="Enter region name"
                                                        value={formData.name}
                                                        onChange={(e) => handleInputChange("name", e.target.value)}
                                                        className={errors.name ? "border-danger" : ""}
                                                    />
                                                    {errors.name && (
                                                        <div className="mt-2 text-danger text-xs">{errors.name}</div>
                                                    )}
                                                </div>

                                                <div className="col-span-12">
                                                    <label className="inline-block mb-2">
                                                        States
                                                    </label>
                                                    <div className="border border-slate-200 dark:border-darkmode-400 rounded-lg p-4 max-h-64 overflow-y-auto">
                                                        {loadingStates ? (
                                                            <div className="flex items-center justify-center py-4">
                                                                <Lucide icon="Loader2" className="w-5 h-5 animate-spin mr-2" />
                                                                <span>Loading states...</span>
                                                            </div>
                                                        ) : states.length === 0 ? (
                                                            <div className="text-slate-500 text-sm">No states available</div>
                                                        ) : (
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                {states.map((state) => {
                                                                    const isSelected = formData.stateIds?.includes(state.id) || false;
                                                                    const hasConflict = !!stateConflicts[state.id];
                                                                    const conflictInfo = stateConflicts[state.id];

                                                                    return (
                                                                        <label
                                                                            key={state.id}
                                                                            className={`flex items-center p-2 rounded transition-colors ${
                                                                                hasConflict && !isSelected
                                                                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 cursor-not-allowed'
                                                                                    : hasConflict
                                                                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 cursor-pointer'
                                                                                    : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-darkmode-400 border border-transparent'
                                                                            } ${hasConflict && !isSelected ? 'opacity-75' : ''}`}
                                                                            onClick={(e) => {
                                                                                // Prevent label click from checking the box if there's a conflict and it's not selected
                                                                                if (hasConflict && !isSelected) {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                }
                                                                            }}
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                disabled={hasConflict && !isSelected}
                                                                                onChange={() => {
                                                                                    if (!hasConflict || isSelected) {
                                                                                        handleStateToggle(state.id);
                                                                                    }
                                                                                }}
                                                                                className={`w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary focus:ring-2 ${
                                                                                    hasConflict && !isSelected ? 'opacity-50 cursor-not-allowed' : ''
                                                                                }`}
                                                                                title={hasConflict && !isSelected ? `This state is already assigned to "${conflictInfo?.regionName}"` : ''}
                                                                            />
                                                                            <div className="ml-2 flex-1">
                                                                                <span className={`text-sm ${
                                                                                    hasConflict
                                                                                        ? 'text-yellow-800 dark:text-yellow-200 font-semibold'
                                                                                        : 'text-slate-700 dark:text-slate-300'
                                                                                }`}>
                                                                                    {state.name}
                                                                                </span>
                                                                                {hasConflict && (
                                                                                    <div className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 font-medium">
                                                                                        ⚠️ Already in "{conflictInfo?.regionName}"
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {formData.stateIds && formData.stateIds.length > 0 && (
                                                        <div className="mt-2 text-xs text-slate-500">
                                                            {formData.stateIds.length} state(s) selected
                                                        </div>
                                                    )}
                                                    {errors.stateIds && (
                                                        <div className="mt-2 text-danger text-xs">{errors.stateIds}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex justify-end mt-8 gap-3">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={handleCancel}
                                                    disabled={loading}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={loading}
                                                >
                                                    {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                                                    {mode === "add" ? "Create Region" : "Update Region"}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}

export default EditRegion;
